import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Activity, Loader2 } from 'lucide-react'
import { formatNumber } from '../utils'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { getAiUsage } from '@/lib/api/features/ai'

interface Props {
  workspaceSlug: string
}

/**
 * Card 2 — AI usage for the current billing period.
 *
 * v1 ships aggregate cards + a by-operation drill-down only; per the plan a
 * daily time series would need a backend response extension (group-by date
 * over AiUsageEvents) that we deferred to a follow-up. The aggregate view is
 * still useful for "did we hit our monthly cap?" + "which operation drove
 * the spend?".
 */
export function AiUsageCard({ workspaceSlug }: Props) {
  const { t } = useTranslation('settings')
  const usageQuery = useQuery({
    queryKey: ['ai-admin-usage', workspaceSlug],
    queryFn: () => getAiUsage(workspaceSlug),
    refetchOnWindowFocus: true,
  })

  const totals = usageQuery.data?.totals
  // While trialing, the backend measures AI usage over the whole trial, so the
  // totals and the quota rows share that window — label it rather than saying
  // "this billing period".
  const isTrialWindow = usageQuery.data?.usageWindowIsTrial === true
  const byOperation = useMemo(
    () =>
      (usageQuery.data?.byOperation ?? []).slice().sort(
        (a, b) => b.costUsd - a.costUsd,
      ),
    [usageQuery.data?.byOperation],
  )

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Activity className="size-4 text-muted-foreground" />
          <CardTitle>
            {isTrialWindow
              ? t('aiSettings.usage.titleTrial')
              : t('aiSettings.usage.title')}
          </CardTitle>
        </div>
        <CardDescription>
          {isTrialWindow
            ? t('aiSettings.usage.descriptionTrial')
            : t('aiSettings.usage.description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {usageQuery.isLoading ? (
          <div className="flex h-32 items-center justify-center text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
          </div>
        ) : !totals ? (
          <p className="text-sm text-muted-foreground">
            {t('aiSettings.usage.empty')}
          </p>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-3">
              <StatCard
                label={t('aiSettings.usage.totalTokens')}
                value={formatNumber(totals.totalTokens)}
              />
              <StatCard
                label={t('aiSettings.usage.calls')}
                value={`${formatNumber(totals.successCalls)} / ${formatNumber(totals.calls)}`}
                hint={t('aiSettings.usage.callsHint')}
              />
              <StatCard
                label={t('aiSettings.usage.estimatedCost')}
                value={`$${totals.estimatedCostUsd.toFixed(2)}`}
              />
            </div>

            {byOperation.length > 0 ? (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('aiSettings.usage.operation')}</TableHead>
                      <TableHead className="text-right">
                        {t('aiSettings.usage.calls')}
                      </TableHead>
                      <TableHead className="text-right">
                        {t('aiSettings.usage.tokens')}
                      </TableHead>
                      <TableHead className="text-right">
                        {t('aiSettings.usage.cost')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {byOperation.map((row) => (
                      <TableRow key={row.operation}>
                        <TableCell className="font-medium">
                          {row.operation}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatNumber(row.calls)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatNumber(row.tokens)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          ${row.costUsd.toFixed(4)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  )
}

function StatCard({
  label,
  value,
  hint,
}: {
  label: string
  value: string
  hint?: string
}) {
  return (
    <div className="rounded-md border p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 text-xl font-semibold tabular-nums">{value}</p>
      {hint ? (
        <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  )
}
